import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { addDays, localDate, today } from '../../lib/dates';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { navigate } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, cx, Empty, ErrorBox, Loading, PageHeader, Select, useToast } from '../../ui/components';

interface Slot { startsAt: string; endsAt: string; trainerPersonId: string; trainerName: string; locationId: string; locationName: string }
interface DirectType {
  id: string; name: string; description: string | null; durationMin: number; tokenName: string | null; tokensRequired: number;
  dropInPriceCents: number | null; color: string | null; trainers: { personId: string; name: string }[];
}

/** Members book a one-to-one lesson straight into a trainer's available hours. */
export function BookTrainerPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [lessonTypeId, setLessonTypeId] = useState('');
  const [trainer, setTrainer] = useState('');
  const [day, setDay] = useState('');
  const types = useApi(() => api<DirectType[]>(`${T(gym.slug)}/booking/lesson-types`), []);
  const chosen = (types.data ?? []).find((t) => t.id === lessonTypeId) ?? types.data?.[0];
  const slots = useApi(chosen ? () => api<Slot[]>(`${T(gym.slug)}/booking/slots`, { query: { lessonTypeId: chosen.id, trainer } }) : null, [chosen?.id, trainer]);
  const tokens = useApi(() => api<{ tokenTypeId: string; name: string; available: number }[]>(`${T(gym.slug)}/me/tokens`), []);

  const byDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots.data ?? []) {
      const d = localDate(s.startsAt, i18n.tz);
      map.set(d, [...(map.get(d) ?? []), s]);
    }
    return map;
  }, [slots.data, i18n.tz]);
  const days = [...byDay.keys()].sort();
  const activeDay = days.includes(day) ? day : days[0];
  const balance = tokens.data?.find((t) => t.name === chosen?.tokenName)?.available ?? 0;

  const book = async (slot: Slot, payment?: 'drop_in'): Promise<void> => {
    const r = await api(`${T(gym.slug)}/booking/slots`, {
      body: {
        lessonTypeId: chosen!.id, trainerPersonId: slot.trainerPersonId, startsAt: slot.startsAt, locationId: slot.locationId, payment,
        successUrl: `${window.location.origin}${gym.base}/bookings?paid=1`, cancelUrl: `${window.location.origin}${gym.base}/book`,
      },
    });
    if (r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
    toast.ok(i18n.t('book.booked', { trainer: slot.trainerName, when: i18n.dateTime(slot.startsAt) }));
    navigate(`${gym.base}/bookings`);
  };

  if (types.error) return <div className="page"><ErrorBox error={types.error} onRetry={types.reload} /></div>;
  if (!types.data) return <Loading />;
  if (!types.data.length) return <div className="page"><PageHeader title={i18n.t('book.title')} /><Empty icon="🗓">{i18n.t('book.none_available')}</Empty></div>;

  return (
    <div className="page">
      <PageHeader title={i18n.t('book.title')} subtitle={i18n.t('book.subtitle')} />
      <Card>
        <div className="grid-2">
          <label className="field">
            <span className="field-label">{i18n.t('planner.lesson')}</span>
            <Select value={chosen?.id ?? ''} onChange={(e) => { setLessonTypeId(e.target.value); setTrainer(''); }}
              options={types.data.map((t) => ({ value: t.id, label: `${t.name} · ${t.durationMin} min` }))} />
          </label>
          <label className="field">
            <span className="field-label">{i18n.t('planner.trainer')}</span>
            <Select value={trainer} placeholder={i18n.t('book.any_trainer')} onChange={(e) => setTrainer(e.target.value)}
              options={(chosen?.trainers ?? []).map((t) => ({ value: t.personId, label: t.name }))} />
          </label>
        </div>
        {chosen?.description && <p className="small muted">{chosen.description}</p>}
        <p className="small">
          {chosen?.tokenName
            ? i18n.t('book.costs_tokens', { count: chosen.tokensRequired, token: chosen.tokenName, available: balance })
            : i18n.t('book.no_token_needed')}
          {chosen?.dropInPriceCents ? ` · ${i18n.t('book.or_pay', { price: i18n.money(chosen.dropInPriceCents) })}` : ''}
        </p>
      </Card>

      {slots.error ? <ErrorBox error={slots.error} onRetry={slots.reload} /> : !slots.data ? <Loading /> : days.length === 0 ? (
        <Empty icon="🗓">{i18n.t('book.no_slots')}</Empty>
      ) : (
        <>
          <div className="day-strip">
            {days.slice(0, 21).map((d) => (
              <button type="button" key={d} className={cx('day-chip', d === activeDay && 'active')} onClick={() => setDay(d)}>
                <small>{i18n.day(d, { weekday: 'short' })}</small>
                <strong>{Number(d.slice(8))}</strong>
                <span className="small">{byDay.get(d)!.length}</span>
              </button>
            ))}
          </div>
          <Card title={i18n.day(activeDay, { weekday: 'long', day: 'numeric', month: 'long' })}>
            <div className="slots">
              {(byDay.get(activeDay) ?? []).map((s) => (
                <div key={`${s.startsAt}-${s.trainerPersonId}`} className="slot">
                  <div>
                    <strong>{i18n.time(s.startsAt)}–{i18n.time(s.endsAt)}</strong>
                    <div className="small muted">{s.trainerName} · {s.locationName}</div>
                  </div>
                  {balance > 0 || !chosen?.tokenName ? (
                    <AsyncButton size="sm" onClick={() => book(s)}>{i18n.t('planner.book')}</AsyncButton>
                  ) : chosen?.dropInPriceCents ? (
                    <AsyncButton size="sm" variant="secondary" onClick={() => book(s, 'drop_in')}>{i18n.t('planner.pay_book', { price: i18n.money(chosen.dropInPriceCents) })}</AsyncButton>
                  ) : <Badge value="no_tokens" label={i18n.t('planner.no_tokens')} />}
                </div>
              ))}
            </div>
          </Card>
          {!chosen?.tokenName || balance > 0 ? null : (
            <Button variant="secondary" onClick={() => navigate(`${gym.base}/shop`)}>{i18n.t('book.buy_tokens')}</Button>
          )}
        </>
      )}
      <p className="small muted">{i18n.t('book.window_hint', { from: i18n.day(today(gym.gym.timezone)), to: i18n.day(addDays(today(gym.gym.timezone), 30)) })}</p>
    </div>
  );
}
